import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, switchMap } from 'rxjs';

import { MeetingMessageAction } from '@ninety/platform/models/meetings/meeting-message-action';
import { ChannelService } from '@ninety/platform/services';
import { selectCompanyId, RealTimeActions, selectCurrentUserId } from '@ninety/platform/state';

import {
  MeetingDialogActions,
  MeetingNotesActions,
  MeetingRealTimeActions,
  MeetingsPageActions,
  MeetingStateActions,
} from '../meetings.actions';
import { MeetingsStateSelectors } from '../meetings.selectors';

/**
 * Messages broadcast from the current users client to others
 */

@Injectable()
export class MeetingsRealTimeBroadcastEffects {
  private readonly actions$ = inject(Actions);
  private readonly channelService = inject(ChannelService);
  private readonly store = inject(Store);

  /** Subscribe to messages for the selected meeting type when joining a meeting */
  subscribeToMeetingChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingsPageActions.getMeetingSuccess, MeetingsPageActions.resumeMeetingSuccess),
        concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
        filter(([, meeting]) => !!meeting),
        switchMap(([, meeting]) => this.channelService.subscribeToChannel(`meeting-${meeting._id}`, true))
      ),
    { dispatch: false }
  );

  /**
   * Broadcast meeting start/stop/pause changes. These same changes are being broadcast
   * from the meeting service for now so we need to handle duplicate message -
   * but the logic there is too convoluted to unwind until everything is state.
   */

  /** Broadcast changes when starting/stopping/etc a meeting */
  meetingStateChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        MeetingDialogActions.createMeetingSuccess,
        MeetingsPageActions.getMeetingSuccess,
        MeetingsPageActions.resumeMeetingSuccess,
        MeetingRealTimeActions.meetingStateChange
      ),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(selectCompanyId),
        this.store.select(MeetingsStateSelectors.selectTeamId),
      ]),
      map(([, meeting, companyId, teamId]) => {
        return RealTimeActions.sendMessage({
          channelId: companyId,
          message: {
            messageType: 'meeting-state-change',
            document: {
              meeting: {
                _id: meeting._id,
                type: meeting.type,
                inProgress: meeting.inProgress,
                paused: meeting.paused,
              },
              teamId,
            },
          },
        });
      })
    )
  );

  /** Broadcasts to users on the meetings page that a meeting has ended */
  deleteMeetingStateChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.deleteActiveMeetingSuccess),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectMeetingStatus),
        this.store.select(selectCompanyId),
        this.store.select(MeetingsStateSelectors.selectTeamId),
      ]),
      filter(([, meeting]) => !!meeting?._id),
      map(([, meeting, companyId, teamId]) => {
        return RealTimeActions.sendMessage({
          channelId: companyId,
          message: {
            messageType: 'meeting-state-change',
            document: {
              meeting: {
                _id: meeting._id,
                type: meeting.type,
                inProgress: false,
                paused: false,
              },
              teamId,
            },
          },
        });
      })
    )
  );

  /** Broadcasts to meeting attendees that the meeting has been deleted or suspended */
  broadcastMeetingDeletedOrSuspended$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.deleteActiveMeetingSuccess, MeetingStateActions.suspendMeeting),
      concatLatestFrom(() => [this.store.select(selectCompanyId)]),
      map(([{ type, meetingId }, companyId]) => {
        return RealTimeActions.sendMessage({
          channelId: companyId,
          message: {
            messageType:
              type === MeetingsPageActions.deleteActiveMeetingSuccess.type
                ? MeetingMessageAction.Delete
                : MeetingMessageAction.Suspended,
            document: meetingId,
          },
        });
      })
    )
  );

  /** Broadcasts to meeting attendees that tangent button was clicked */
  broadcastTangentButtonClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.tangentButtonClicked),
      concatLatestFrom(() => [this.store.select(MeetingsStateSelectors.selectCurrentMeeting)]),
      map(([, meeting]) => {
        const message = {
          channelId: `meeting-${meeting._id}`,
          message: {
            messageType: MeetingMessageAction.MeetingTangent,
            document: { timestamp: new Date().toISOString() },
          },
        };
        return RealTimeActions.sendMessage(message);
      })
    )
  );

  /**
   * Send the attachment list to the other participants after the notes panel adds or removes one.
   *
   * Reducers run before effects, so the list read here is already updated. Reading it from state
   * means what we send always matches what we saved.
   */
  broadcastAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingNotesActions.attachmentAdded, MeetingNotesActions.attachmentRemoved),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(selectCurrentUserId),
      ]),
      // Same rule as MeetingService.broadcastMessage: only send while the meeting is running.
      filter(([, meeting]) => !!meeting?.inProgress),
      map(([, meeting, userId]) =>
        RealTimeActions.sendMessage({
          channelId: `meeting-${meeting._id}`,
          message: {
            messageType: 'meeting',
            document: {
              action: MeetingMessageAction.Update,
              meeting: { attachments: meeting.attachments ?? [] },
              emitterId: userId,
            },
          },
        })
      )
    )
  );

  /** Opens tangent dialog if current user is the presenter */
  openTangentDialogForPresenter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingsPageActions.tangentButtonClicked),
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(selectCurrentUserId),
      ]),
      filter(([, meeting, userId]) => meeting.presenterUserId === userId),
      map(() => MeetingRealTimeActions.openMeetingTangentDialog())
    )
  );
}
